class PremiumCollectionProductCards extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.effectTimers = new WeakMap();
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.viewAllCustom = this.dataset.viewAllMode === 'custom';
    this.activeCollectionBadgeTarget = null;
    this.collectionSwitchTimer = null;
    this.collectionEnterFrame = null;

    this.onPointerDown = this.handlePointerDown.bind(this);
    this.onCartUpdate = this.handleCartUpdate.bind(this);
    this.onClick = this.handleClick.bind(this);
    this.onKeydown = this.handleKeydown.bind(this);

    this.addEventListener('pointerdown', this.onPointerDown);
    this.addEventListener('shopify:cart:lines-update', this.onCartUpdate);
    this.addEventListener('click', this.onClick);
    this.addEventListener('keydown', this.onKeydown);

    this.querySelectorAll('[data-variant-selector]').forEach((selector) => {
      this.setVariantSelectorState(selector, selector.dataset.open === 'true', { focus: false });
    });

    const defaultBadgeTarget = this.querySelector('.ppc__collection-filter.is-active[data-badge-target]')?.dataset.badgeTarget;
    if (defaultBadgeTarget) {
      this.activeCollectionBadgeTarget = defaultBadgeTarget;
      this.applyCollectionPanelState(defaultBadgeTarget, { animate: false, updateViewAllOnly: true });
    }

    if (this.dataset.animate === 'true' && 'IntersectionObserver' in window && !this.reduceMotion) {
      this.observer = new IntersectionObserver(this.handleIntersection, {
        rootMargin: '120px',
        threshold: 0.08,
      });
      this.observer.observe(this);
    } else {
      this.dataset.inView = 'true';
    }
  }

  disconnectedCallback() {
    this.removeEventListener('pointerdown', this.onPointerDown);
    this.removeEventListener('shopify:cart:lines-update', this.onCartUpdate);
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('keydown', this.onKeydown);
    this.observer?.disconnect();
    window.clearTimeout(this.collectionSwitchTimer);
    window.cancelAnimationFrame(this.collectionEnterFrame);
  }

  handlePointerDown(event) {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    const card = event.target.closest?.('[data-premium-product-card]');
    if (!card || !this.contains(card)) return;
    this.restartEffect(card, 'is-clicked', 520);
  }

  handleClick(event) {
    const badge = event.target.closest?.('[data-badge-trigger]');
    if (badge && this.contains(badge)) {
      event.preventDefault();
      const target = badge.dataset.badgeTarget;
      if (target && target !== this.activeCollectionBadgeTarget) {
        this.switchCollectionPanel(target);
      }
      return;
    }

    const toggle = event.target.closest?.('[data-variant-toggle]');
    if (toggle && this.contains(toggle)) {
      event.preventDefault();
      const selector = toggle.closest('[data-variant-selector]');
      const willOpen = selector?.dataset.open !== 'true';

      if (willOpen) {
        this.querySelectorAll('[data-variant-selector][data-open="true"]').forEach((other) => {
          if (other !== selector) this.setVariantSelectorState(other, false);
        });
      }

      this.setVariantSelectorState(selector, willOpen, { focus: willOpen });
      return;
    }

    const close = event.target.closest?.('[data-variant-close]');
    if (close && this.contains(close)) {
      event.preventDefault();
      const selector = close.closest('[data-variant-selector]');
      this.setVariantSelectorState(selector, false);
      requestAnimationFrame(() => selector?.querySelector('[data-variant-toggle]')?.focus());
      return;
    }

    const option = event.target.closest?.('[data-variant-option]');
    if (!option || !this.contains(option) || option.disabled) return;

    event.preventDefault();
    this.selectVariant(option);
  }

  handleKeydown(event) {
    if (event.key !== 'Escape') return;

    const selector = event.target.closest?.('[data-variant-selector]');
    if (!selector || selector.dataset.open !== 'true' || !this.contains(selector)) return;

    event.preventDefault();
    this.setVariantSelectorState(selector, false, { focus: false });
    requestAnimationFrame(() => selector.querySelector('[data-variant-toggle]')?.focus());
  }

  setVariantSelectorState(selector, open, options = {}) {
    if (!selector) return;

    const { focus = false } = options;
    const toggle = selector.querySelector('[data-variant-toggle]');
    const panel = selector.querySelector('[data-variant-panel]');
    if (!toggle || !panel) return;

    selector.dataset.open = String(open);
    selector.classList.toggle('is-open', open);
    toggle.setAttribute('aria-expanded', String(open));
    const form = selector.querySelector('.ppc__form');
    panel.setAttribute('aria-hidden', String(!open));
    panel.toggleAttribute('inert', !open);
    form?.toggleAttribute('inert', !open);

    if (open && focus) {
      requestAnimationFrame(() => selector.querySelector('[data-variant-option][aria-pressed="true"]')?.focus());
    }
  }

  switchCollectionPanel(targetId, options = {}) {
    if (!targetId) return;

    const {
      updateFocus = false,
      updateViewAllOnly = false,
    } = options;

    const targetPanel = this.querySelector(`[data-badge-panel="${targetId}"]`);
    if (!targetPanel) return;

    const targetButton = this.querySelector(`[data-badge-trigger][data-badge-target="${targetId}"]`);
    if (!targetButton) return;

    this.activeCollectionBadgeTarget = targetId;

    this.closeAllVariantSelectors();
    this.applyCollectionPanelState(targetId, { updateViewAllOnly });

    this.querySelectorAll('.ppc__collection-filter').forEach((btn) => {
      const isActive = btn === targetButton;
      btn.classList.toggle('is-active', isActive);
      btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });

    if (updateFocus) requestAnimationFrame(() => targetButton.focus());
  }

  applyCollectionPanelState(targetId, options = {}) {
    const {
      animate = !this.reduceMotion,
      updateViewAllOnly = false,
    } = options;
    const targetPanel = this.querySelector(`[data-badge-panel="${targetId}"]`);
    if (!targetPanel) return;

    const activePanel = this.querySelector('.ppc__collection-panel.is-active');
    const isNewPanel = activePanel !== targetPanel;

    if (isNewPanel) {
      window.clearTimeout(this.collectionSwitchTimer);
      window.cancelAnimationFrame(this.collectionEnterFrame);

      const panels = this.querySelectorAll('.ppc__collection-panel');
      const panelsContainer = targetPanel.closest('.ppc__collection-panels');
      let currentHeight = 0;

      if (animate && panelsContainer) {
        currentHeight = panelsContainer.getBoundingClientRect().height;
        panelsContainer.classList.add('is-measuring');
        panelsContainer.style.height = `${currentHeight}px`;
        void panelsContainer.offsetHeight;
        panelsContainer.classList.remove('is-measuring');
      }

      panels.forEach((panel) => {
        panel.classList.remove('is-entering', 'is-leaving');

        if (panel !== activePanel && panel !== targetPanel) {
          panel.classList.remove('is-active');
          panel.setAttribute('aria-hidden', 'true');
          panel.setAttribute('inert', '');
        }
      });

      if (activePanel) {
        activePanel.classList.remove('is-active');
        activePanel.setAttribute('aria-hidden', 'true');
        activePanel.setAttribute('inert', '');
        if (animate) activePanel.classList.add('is-leaving');
      }

      targetPanel.classList.add('is-active');
      targetPanel.setAttribute('aria-hidden', 'false');
      targetPanel.removeAttribute('inert');

      if (animate) {
        const targetHeight = targetPanel.scrollHeight;
        targetPanel.classList.add('is-entering');
        void targetPanel.offsetWidth;

        this.collectionEnterFrame = window.requestAnimationFrame(() => {
          targetPanel.classList.remove('is-entering');
          if (panelsContainer) panelsContainer.style.height = `${targetHeight}px`;
        });

        this.collectionSwitchTimer = window.setTimeout(() => {
          panels.forEach((panel) => panel.classList.remove('is-leaving'));
          if (panelsContainer) {
            panelsContainer.classList.add('is-measuring');
            panelsContainer.style.removeProperty('height');
            void panelsContainer.offsetHeight;
            panelsContainer.classList.remove('is-measuring');
          }
        }, 620);
      } else {
        panels.forEach((panel) => panel.classList.remove('is-entering', 'is-leaving'));
        if (panelsContainer) panelsContainer.style.removeProperty('height');
      }
    } else {
      targetPanel.setAttribute('aria-hidden', 'false');
      targetPanel.removeAttribute('inert');
    }

    if (!this.viewAllCustom) {
      const viewAll = this.querySelector('.ppc__view-all');
      const panelLink = targetPanel.dataset.badgeUrl;
      if (viewAll && panelLink) {
        viewAll.href = panelLink;
      }
    }
  }

  closeAllVariantSelectors() {
    this.querySelectorAll('[data-variant-selector][data-open="true"]').forEach((selector) => {
      this.setVariantSelectorState(selector, false);
    });
  }

  selectVariant(option) {
    const selector = option.closest('[data-variant-selector]');
    const card = option.closest('[data-premium-product-card]');
    if (!selector || !card) return;

    selector.querySelectorAll('[data-variant-option]').forEach((item) => {
      const selected = item === option;
      item.classList.toggle('is-selected', selected);
      item.setAttribute('aria-pressed', String(selected));
    });

    const variantInput = selector.querySelector('input[name="id"]');
    const quantityInput = selector.querySelector('input[name="quantity"]');
    const addContainer = selector.querySelector('add-to-cart-component');
    const addButton = selector.querySelector('[ref="addToCartButton"]');
    const available = option.dataset.available === 'true';

    if (variantInput) {
      variantInput.value = option.dataset.variantId || '';
      variantInput.disabled = !available;
      variantInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
    if (quantityInput) quantityInput.value = option.dataset.quantityMin || '1';

    if (available && typeof addContainer?.enable === 'function') {
      addContainer.enable();
    } else if (!available && typeof addContainer?.disable === 'function') {
      addContainer.disable();
    } else if (addButton) {
      addButton.disabled = !available;
    }

    const currentPrice = card.querySelector('[data-current-price]');
    const comparePrice = card.querySelector('[data-compare-price]');
    const selectedLabel = selector.querySelector('[data-selected-variant]');
    const variantPrice = option.dataset.variantPrice;
    if (currentPrice && variantPrice !== undefined) currentPrice.textContent = variantPrice;
    if (comparePrice) {
      comparePrice.textContent = option.dataset.comparePrice || '';
      comparePrice.hidden = !option.dataset.comparePrice;
    }
    if (selectedLabel) selectedLabel.textContent = option.dataset.variantTitle || '';

    this.restartEffect(card, 'is-variant-selected', 520);
  }

  handleCartUpdate(event) {
    if (event.action !== 'add') return;

    const source = event.target instanceof Element ? event.target : null;
    const card = source?.closest('[data-premium-product-card]');
    if (!card || !this.contains(card)) return;

    card.classList.add('is-adding');

    const settle = event.promise || Promise.resolve({ detail: { didError: false } });
    settle
      .then(({ detail } = {}) => {
        card.classList.remove('is-adding');
        if (detail?.didError) {
          this.restartEffect(card, 'has-error', 900);
          return;
        }

        this.restartEffect(card, 'is-added', 1100);
        this.setVariantSelectorState(card.querySelector('[data-variant-selector]'), false);
        this.openCartDrawer();
      })
      .catch(() => {
        card.classList.remove('is-adding');
        this.restartEffect(card, 'has-error', 900);
      });
  }

  async openCartDrawer() {
    const drawer = document.querySelector('theme-drawer#cart-drawer');

    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
      return;
    }

    if (window.Shopify?.actions?.openCart) {
      await window.Shopify.actions.openCart();
      return;
    }

    window.location.href = window.Theme?.routes?.cart_url || '/cart';
  }

  restartEffect(card, className, duration) {
    const timers = this.effectTimers.get(card) || new Map();
    window.clearTimeout(timers.get(className));
    card.classList.remove(className);

    requestAnimationFrame(() => {
      card.classList.add(className);
      const timer = window.setTimeout(() => card.classList.remove(className), duration);
      timers.set(className, timer);
      this.effectTimers.set(card, timers);
    });
  }

  handleIntersection = (entries) => {
    if (!entries[0]?.isIntersecting) return;
    this.dataset.inView = 'true';
    this.observer?.disconnect();
  };
}

if (!customElements.get('premium-collection-product-cards')) {
  customElements.define('premium-collection-product-cards', PremiumCollectionProductCards);
}
